import { getApiErrorMessage } from "@shared/utils/errorHandling";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { InvitationService } from "../services/invitation.service";
import {
  ImportContact,
  ImportContactStatus,
  ImportPhase,
} from "../types/contactImport.types";
import { CreateInvitationRequest, InvitationType } from "../types/invitation";

const BATCH_SIZE = 2;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface UseContactImportProps {
  role: string;
  isAdmin: boolean;
  organizationId?: string;
}

export const useContactImport = ({
  role,
  isAdmin,
  organizationId,
}: UseContactImportProps) => {
  const [contacts, setContacts] = useState<ImportContact[]>([]);
  const [phase, setPhase] = useState<ImportPhase>(ImportPhase.UPLOAD);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });

  const parseFile = useCallback(
    (file: File) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json<Record<string, string>>(
            firstSheet,
            { defval: "" }
          );

          if (rows.length === 0) {
            toast.error("The file is empty or has no valid data");
            return;
          }

          const parsed: ImportContact[] = rows
            .map((row) => {
              const email = (
                row.email ||
                row.Email ||
                row.EMAIL ||
                ""
              ).trim();
              const contactRole = (
                row.role ||
                row.Role ||
                row.ROLE ||
                role
              ).trim();

              return { email, role: contactRole || role };
            })
            .filter((c) => c.email.length > 0)
            .map((c) => ({
              ...c,
              status: EMAIL_REGEX.test(c.email)
                ? ImportContactStatus.PENDING
                : ImportContactStatus.ERROR,
              errorMessage: EMAIL_REGEX.test(c.email)
                ? undefined
                : "Invalid email format",
            }));

          if (parsed.length === 0) {
            toast.error(
              "No valid emails found. Ensure the file has an 'email' column."
            );
            return;
          }

          setContacts(parsed);
          setPhase(ImportPhase.PREVIEW);
        } catch {
          toast.error("Failed to parse the file. Please check the format.");
        }
      };

      reader.readAsArrayBuffer(file);
    },
    [role]
  );

  const sendInvitation = async (
    contact: ImportContact
  ): Promise<ImportContact> => {
    try {
      const payload: CreateInvitationRequest = {
        email: contact.email,
        role: contact.role,
        type: isAdmin ? InvitationType.ADMIN : InvitationType.ORGANIZATION,
      };

      if (isAdmin) {
        await InvitationService.createAdminInvitation(payload);
      } else if (organizationId) {
        await InvitationService.createInvitation(organizationId, payload);
      }

      return { ...contact, status: ImportContactStatus.SUCCESS };
    } catch (err: unknown) {
      const message = getApiErrorMessage(err);
      const isDuplicate =
        message.toLowerCase().includes("already") ||
        message.toLowerCase().includes("duplicate") ||
        message.toLowerCase().includes("exists");

      return {
        ...contact,
        status: isDuplicate
          ? ImportContactStatus.DUPLICATE
          : ImportContactStatus.ERROR,
        errorMessage: message,
      };
    }
  };

  const startImport = useCallback(async () => {
    const pendingContacts = contacts.filter(
      (c) => c.status === ImportContactStatus.PENDING
    );

    if (pendingContacts.length === 0) {
      toast.error("No valid contacts to import");
      return;
    }

    setIsProcessing(true);
    setPhase(ImportPhase.IMPORTING);
    setProgress({ processed: 0, total: pendingContacts.length });

    const updatedContacts = [...contacts];

    for (let i = 0; i < pendingContacts.length; i += BATCH_SIZE) {
      const batch = pendingContacts.slice(i, i + BATCH_SIZE);

      const batchResults = await Promise.all(
        batch.map((contact) => sendInvitation(contact))
      );

      batchResults.forEach((result) => {
        const idx = updatedContacts.findIndex(
          (c) => c.email === result.email
        );
        if (idx !== -1) {
          updatedContacts[idx] = result;
        }
      });

      setContacts([...updatedContacts]);
      setProgress((prev) => ({
        ...prev,
        processed: Math.min(i + BATCH_SIZE, pendingContacts.length),
      }));
    }

    setIsProcessing(false);
    setPhase(ImportPhase.COMPLETED);

    const successCount = updatedContacts.filter(
      (c) => c.status === ImportContactStatus.SUCCESS
    ).length;
    const errorCount = updatedContacts.filter(
      (c) => c.status === ImportContactStatus.ERROR
    ).length;

    if (errorCount > 0) {
      toast.error(
        `Import completed: ${successCount} sent, ${errorCount} failed. Check details below.`
      );
    } else {
      toast.success(
        `Import completed: ${successCount} invitation(s) sent successfully.`
      );
    }
  }, [contacts, isAdmin, organizationId]);

  const reset = useCallback(() => {
    setContacts([]);
    setPhase(ImportPhase.UPLOAD);
    setIsProcessing(false);
    setProgress({ processed: 0, total: 0 });
  }, []);

  const removeContact = useCallback((email: string) => {
    setContacts((prev) => prev.filter((c) => c.email !== email));
  }, []);

  return {
    contacts,
    phase,
    isProcessing,
    progress,
    parseFile,
    startImport,
    reset,
    removeContact,
  };
};
